import { LocalStorageService } from '../../../services/local-storage.service';
import { UndoJournal } from '../../../shared/undo-journal';
import { Injectable, OnDestroy } from '@angular/core';
import type {
  DataFilterValues,
} from '../../../shared/ui/data-filter/data-filter.component';

/**
 * How the Board splits itself into rows. The columns are always the stages;
 * this chooses what the rows are, and `none` means one row holding everything.
 */
export type BoardRowGroup = 'none' | 'salesRep' | 'routeFit' | 'contributionTier';
/** The view modes rendered by the Leads page itself. */
export type BoardView = 'list' | 'board';
export const BOARD_VIEWS: BoardView[] = ['list', 'board'];

/** One stage move a person made and may take back: what moved, from where,
 *  to where, in the terms needed to say so and to do so. */
export interface UndoableMove {
  leadId: number;
  /** For the tooltip and the snackbar - "which card was that again". */
  title: string;
  fromCode: string;
  toCode: string;
  /** Which slot of the old column it was in. Taking a card back to the right
   *  column but the wrong place is only half an undo: the card ends up
   *  somewhere it has never been, which is a third state rather than the one
   *  being restored. */
  fromIndex: number;
}

/**
 * How one column arranges the cards in it.
 *
 * `manual` is the order the board arrived in, with dropped cards where they
 * were dropped - the default, because that is the arrangement a person makes
 * with their own hands and nothing should quietly overrule it.
 */
export type ColumnSort = 'manual' | 'value' | 'age' | 'due' | 'title';

export const COLUMN_SORTS: { id: ColumnSort; label: string }[] = [
  { id: 'manual', label: 'As arranged' },
  { id: 'value', label: 'Contribution, highest first' },
  { id: 'age', label: 'Longest in this stage first' },
  { id: 'due', label: 'Next action, soonest first' },
  { id: 'title', label: 'Title, A to Z' },
];

/**
 * Single filter/view state shared by the Leads List and Board. Views are different
 * arrangements of the same filtered set — switching must never reset the
 * filter context.
 */
@Injectable()
export class LeadsBoardStateService implements OnDestroy {
  private readonly sessionSubscription;
  constructor(private readonly storage: LocalStorageService) {
    this.sessionSubscription = storage.sessionChanged$.subscribe(() => {
      this.columnSortValue = readColumnSorts(this.storage);
      // The stack this used to clear is now UndoJournal, shared with the
      // roll-off board; clearing it on a session change is the same intent.
      this.undo.clear();
    });
  }

  ngOnDestroy(): void { this.sessionSubscription.unsubscribe(); }
  /** The collection route sets this when it mounts. It is not cached across
   *  routes: /leads/table and /leads/board are the source of truth. */
  view: BoardView = 'list';
  search = '';
  /** Selected stage codes; repeated ?column= values also populate this.
   *  Empty means all stages. */
  columns: string[] = [];
  /** "Show me my patch": server-side, shared by List and Board. */
  territoryId: number | null = null;
  /** Every territory assigned to me, without having to name one - the server
   *  resolves it by owner. Mutually exclusive with territoryId. */
  territoryMine = false;
  assignedToMeOnly = false;
  highContributionOnly = false;
  /**
   * Column rules added from the filter bar's "+" menu. Keyed by the query
   * parameter each becomes, so load() passes them straight through rather
   * than growing a field here per rule.
   *
   * Lives beside the other filters for the same reason they do: flipping
   * between List and Board must not quietly widen what you are looking at.
   */
  ruleFilters: DataFilterValues = {};
  rowGroup: BoardRowGroup = 'none';

  /**
   * The moves this person has made and not taken back, oldest first - what
   * the Undo button walks backwards through.
   *
   * Codes, not ids: a code survives the reloads between doing and undoing.
   * It lives here rather than on the Board component so flipping to List and
   * back does not silently discard the right to change your mind - this
   * service already exists to make that flip lossless. It is NOT persisted:
   * across a real reload there is no way to know the board still looks like
   * the stack remembers, and an Undo button that might act on a stale memory
   * is worse than one that starts empty.
   *
   * The stack itself is UndoJournal, shared with the roll-off board. What is
   * this page's is the STEP - a lead, its title, the two codes and the slot.
   */
  readonly undo = new UndoJournal<UndoableMove>();

  /**
   * How each column is sorted, keyed by stage CODE. Absent = `manual`.
   *
   * Per column rather than one rule for the board, because the columns are
   * not asking the same question: Prospect is a pile to work through oldest
   * first, while Contract is a short list you read by value. A single control
   * would force one of those to be wrong.
   *
   * Persisted, like density - it is a way of looking, not a filter, and
   * having to re-choose it every visit is what makes a preference useless.
   * Keyed by code so a tenant renaming a stage keeps its arrangement.
   */
  private columnSortValue: Record<string, ColumnSort> = readColumnSorts(this.storage);

  sortFor(column: string): ColumnSort {
    return this.columnSortValue[column] ?? 'manual';
  }

  setSortFor(column: string, sort: ColumnSort): void {
    if (sort === 'manual') delete this.columnSortValue[column];
    else this.columnSortValue[column] = sort;
    this.storage.set('rb-column-sort', this.columnSortValue);
  }


}

/**
 * The stored column sorts, or nothing at all.
 *
 * Anything unrecognised is dropped rather than trusted: this comes back as
 * whatever was in localStorage last time, which may have been written by an
 * older build with different rule names, and a column sorted by a rule that
 * no longer exists would render in whatever order the comparator's fallback
 * produced - an arrangement nobody chose and nobody can explain.
 */
function readColumnSorts(storage: LocalStorageService): Record<string, ColumnSort> {
  const known = new Set(COLUMN_SORTS.map(sort => sort.id));
  try {
    const stored = storage.get<unknown>('rb-column-sort');
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {};
    return Object.fromEntries(
      Object.entries(stored as Record<string, unknown>)
        .filter((entry): entry is [string, ColumnSort] =>
          known.has(entry[1] as ColumnSort)));
  } catch {
    return {};   // not JSON at all; start clean rather than throw on load
  }
}
